import { Injectable, inject } from '@angular/core';
import {
  InfoCard,
  PersonalReference,
  DataSection,
} from '../../models/applicant-record.model';
import { Location } from '../../models/Location/Location';
import { GeocodingService } from './geocoding.service';
import { ScoreService } from './score.service';
import { CLOSING_FIELD_CODE, formatCurrency, parseNumber } from '../../data';
import { TicketClosingField } from '../../models';
import { TranslationService } from '../base/translation.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicantRecordService {
  private readonly geocodingService = inject(GeocodingService);
  private readonly scoreService = inject(ScoreService);
  private readonly translationService = inject(TranslationService);

  buildInfoCards(
    fields: TicketClosingField[],
    coords: { user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null },
    location: Location | null
  ): InfoCard[] {
    const cards: InfoCard[] = [];

    const ingreso1 = fields.find(f => f.Code === CLOSING_FIELD_CODE.INGRESO_MENSUAL_1);
    const ingreso2 = fields.find(f => f.Code === CLOSING_FIELD_CODE.INGRESO_MENSUAL_2);
    const egreso = fields.find(f => f.Code === CLOSING_FIELD_CODE.EGRESO_MENSUAL);

    const hasIncome = ingreso1?.Value || ingreso2?.Value;
    const hasExpense = egreso?.Value;

    if (hasIncome) {
      const ing1 = parseNumber(ingreso1?.Value);
      const ing2 = parseNumber(ingreso2?.Value);
      const totalIngreso = ing1 + (ing2 || 0);
      cards.push({
        title: this.translationService.instant('applicantRecord.monthlyIncome'),
        value: formatCurrency(totalIngreso),
        valueColor: 'success',
        description: this.translationService.instant('applicantRecord.monthlyIncomeDescription'),
      });
    }

    if (hasExpense) {
      const totalEgreso = parseNumber(egreso?.Value);
      cards.push({
        title: this.translationService.instant('applicantRecord.monthlyExpense'),
        value: formatCurrency(totalEgreso),
        valueColor: 'danger',
        description: this.translationService.instant('applicantRecord.monthlyExpenseDescription'),
      });
    }

    if (hasIncome) {
      const ing1 = parseNumber(ingreso1?.Value);
      const ing2 = parseNumber(ingreso2?.Value);
      const totalIngreso = ing1 + (ing2 || 0);
      const totalEgreso = parseNumber(egreso?.Value);
      const capacidadPago = totalIngreso - totalEgreso;
      const porcentajeDisponible = totalIngreso > 0 ? ((capacidadPago / totalIngreso) * 100).toFixed(1) : '0';
      cards.push({
        title: this.translationService.instant('applicantRecord.paymentCapacity'),
        value: formatCurrency(capacidadPago),
        valueColor: capacidadPago >= 0 ? 'success' : 'danger',
        description: `${porcentajeDisponible}${this.translationService.instant('applicantRecord.availableIncomePercent')}`,
      });
    }

    const mainLat = location?.Latitude ? Number(location.Latitude) : null;
    const mainLng = location?.Longitude ? Number(location.Longitude) : null;

    if (mainLat && mainLng) {
      const nivelRiesgoResult = this.scoreService.calculateRiskLevel(
        coords.user, coords.formulario, coords.evidencias, mainLat, mainLng
      );
      if (hasIncome) {
        cards.push({
          title: this.translationService.instant('applicantRecord.riskLevel'),
          value: nivelRiesgoResult.level,
          valueColor: nivelRiesgoResult.color,
          description: nivelRiesgoResult.description,
        });
      }
      const scoreKycResult = this.scoreService.calculateScore(
        coords.user, coords.formulario, coords.evidencias, mainLat, mainLng
      );
      cards.push({
        title: this.translationService.instant('applicantRecord.scoreKyc'),
        value: scoreKycResult.score,
        valueColor: scoreKycResult.color,
        description: scoreKycResult.description,
      });
    }

    return cards;
  }

  buildUserProfile(fields: TicketClosingField[]): DataSection {
    const rows: { label: string; value: string }[] = [];
    const fieldCatalog: { code: string; type: 'simple' | 'combined'; label?: string; codes?: string[] }[] = [
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_1, type: 'simple' },
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_2, type: 'simple' },
      { code: CLOSING_FIELD_CODE.SITUACION_LABORAL_2, codes: [CLOSING_FIELD_CODE.SITUACION_LABORAL_2, CLOSING_FIELD_CODE.SITUACION_LABORAL_1], type: 'combined', label: this.translationService.instant('applicantRecord.laborSituation') },
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_3, type: 'simple' },
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_4, type: 'simple' },
      { code: CLOSING_FIELD_CODE.CREDITOS_VIGENTES, codes: [CLOSING_FIELD_CODE.CREDITOS_VIGENTES, CLOSING_FIELD_CODE.EGRESO_MENSUAL], type: 'combined', label: this.translationService.instant('applicantRecord.activeCredits') },
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_5, type: 'simple' },
      { code: CLOSING_FIELD_CODE.USER_PROFILE_FIELD_6, type: 'simple' },
    ];

    for (const item of fieldCatalog) {
      if (item.type === 'simple') {
        const field = fields.find(f => f.Code === item.code);
        if (field) {
          rows.push({ label: field.Label || field.ClosingFieldDescription || '', value: String(field.Value ?? '') });
        }
      } else if (item.type === 'combined' && item.codes) {
        const fieldValues = item.codes.map(code => {
          const field = fields.find(f => f.Code === code);
          return field?.Value || '';
        }).filter(v => v !== '');

        if (fieldValues.length > 0) {
          rows.push({ label: item.label!, value: fieldValues.join(' - ') });
        }
      }
    }

    return { title: this.translationService.instant('applicantRecord.userProfile'), icon: 'person', accentColor: 'primary', rows };
  }

  async buildLocationData(fields: TicketClosingField[], location: Location | null): Promise<DataSection> {
    const rows: { label: string; value: string }[] = [];

    const ubicacionCodes = [CLOSING_FIELD_CODE.UBICACION_CAPTURA_1, CLOSING_FIELD_CODE.UBICACION_CAPTURA_2];
    for (const code of ubicacionCodes) {
      const field = fields.find(f => f.Code === code);
      if (field?.Value) {
        rows.push({ label: field.Label || field.ClosingFieldDescription || '', value: String(field.Value) });
      }
    }

    let mapEmbedUrl: string | undefined;

    if (location?.Latitude && location?.Longitude) {
      mapEmbedUrl = `https://www.google.com/maps?q=${location.Latitude},${location.Longitude}&z=15&output=embed`;

      try {
        const address = await this.geocodingService.getAddressFromCoordinates(location.Latitude, location.Longitude);
        if (address) {
          rows.unshift({ label: this.translationService.instant('applicantRecord.address'), value: address });
        }
      } catch {
      }
    }

    return { title: this.translationService.instant('applicantRecord.captureLocation'), icon: 'location_on', accentColor: 'success', rows, mapEmbedUrl };
  }

  buildCoordinates(fields: TicketClosingField[]): { user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null } {
    const parseCoords = (value: string | undefined): [number, number] | null => {
      if (!value) return null;
      const coords = value.split(',').map(Number);
      return coords.length === 2 ? [coords[0], coords[1]] : null;
    };

    return {
      user: parseCoords(fields.find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_USER)?.Value),
      formulario: parseCoords(fields.find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_FORMULARIO)?.Value),
      evidencias: parseCoords(fields.find(f => f.Code === CLOSING_FIELD_CODE.LATITUD_LONGITUD_EVIDENCIAS)?.Value),
    };
  }

  buildPersonalReferences(fields: TicketClosingField[]): PersonalReference[] {
    const references: PersonalReference[] = [];

    const ref1Codes = {
      nombre: CLOSING_FIELD_CODE.REFERENCIA_1_NOMBRE,
      parentesco: CLOSING_FIELD_CODE.REFERENCIA_1_PARENTESCO,
      tiempo: CLOSING_FIELD_CODE.REFERENCIA_1_TIEMPO,
      telefono: CLOSING_FIELD_CODE.REFERENCIA_1_TELEFONO,
    };
    const ref2Codes = {
      nombre: CLOSING_FIELD_CODE.REFERENCIA_2_NOMBRE,
      parentesco: CLOSING_FIELD_CODE.REFERENCIA_2_PARENTESCO,
      tiempo: CLOSING_FIELD_CODE.REFERENCIA_2_TIEMPO,
      telefono: CLOSING_FIELD_CODE.REFERENCIA_2_TELEFONO,
    };

    const nombreFieldR1 = fields.find(f => f.Code === ref1Codes.nombre);
    const parentescoFieldR1 = fields.find(f => f.Code === ref1Codes.parentesco);
    const tiempoFieldR1 = fields.find(f => f.Code === ref1Codes.tiempo);
    const telefonoFieldR1 = fields.find(f => f.Code === ref1Codes.telefono);

    const nombreFieldR2 = fields.find(f => f.Code === ref2Codes.nombre);
    const parentescoFieldR2 = fields.find(f => f.Code === ref2Codes.parentesco);
    const tiempoFieldR2 = fields.find(f => f.Code === ref2Codes.tiempo);
    const telefonoFieldR2 = fields.find(f => f.Code === ref2Codes.telefono);

    if (nombreFieldR1?.Value) {
      references.push({
        id: 1,
        nombre: String(nombreFieldR1.Value || ''),
        parentesco: String(parentescoFieldR1?.Value || ''),
        tiempoConocerla: String(tiempoFieldR1?.Value || ''),
        telefono: String(telefonoFieldR1?.Value || ''),
      });
    }

    if (nombreFieldR2?.Value) {
      references.push({
        id: 2,
        nombre: String(nombreFieldR2.Value || ''),
        parentesco: String(parentescoFieldR2?.Value || ''),
        tiempoConocerla: String(tiempoFieldR2?.Value || ''),
        telefono: String(telefonoFieldR2?.Value || ''),
      });
    }

    return references;
  }


}
